import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TaskFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
}

export interface DailyTask {
  placeRefId?: string;
  name?: string;
  file?: TaskFile;
  description: string;
}

export interface DailySchedule {
  day: string;
  tasks: DailyTask[];
}

export interface Schedule {
  dailySchedule: DailySchedule[];
}

export interface Batch {
  from: string;
  to: string;
  groupSize: number;
  maxGroupSizeAllowed: number;
}

export interface TripFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
}

export interface Location {
  lat: number;
  lng: number;
  name: string;
}

export interface TripDraft {
  id: string;
  type: 'Trip' | 'Itinerary';
  title: string;
  description: string;
  schedule: Schedule;
  pricePerPerson: number;
  batches: Batch[];
  files: TripFile[];
  notes: string;
  category: string[];
  location: Location | null;
  // Base64 encoded media data for persistence
  imagesData: any[];
  videosData: any[];
  author: {
    _id: string;
    username: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface TripDraftState {
  drafts: TripDraft[];
}

const initialState: TripDraftState = {
  drafts: [],
};

const TripDraftSlice = createSlice({
  name: 'tripDraft',
  initialState,
  reducers: {
    saveTripDraft: (state, action: PayloadAction<Omit<TripDraft, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const newDraft: TripDraft = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      state.drafts.push(newDraft);
    },
    updateTripDraft: (state, action: PayloadAction<{ id: string; draft: Partial<TripDraft> }>) => {
      const { id, draft } = action.payload;
      const draftIndex = state.drafts.findIndex(d => d.id === id);
      if (draftIndex !== -1) {
        state.drafts[draftIndex] = {
          ...state.drafts[draftIndex],
          ...draft,
          id, // Ensure ID doesn't change
          updatedAt: new Date().toISOString(),
        };
      }
    },
    deleteTripDraft: (state, action: PayloadAction<string>) => {
      state.drafts = state.drafts.filter(draft => draft.id !== action.payload);
    },
    clearAllTripDrafts: (state) => {
      state.drafts = [];
    },
    setTripDrafts: (state, action: PayloadAction<TripDraft[]>) => {
      state.drafts = action.payload;
    },
  },
});

export const { 
  saveTripDraft, 
  updateTripDraft, 
  deleteTripDraft, 
  clearAllTripDrafts, 
  setTripDrafts 
} = TripDraftSlice.actions;
export default TripDraftSlice.reducer;
